// @flow
import { deviceControlModes, deviceHumidityModes, deviceTypes } from '~/core/constants/devices';
import { getProductItemById, getProductItems } from '~/core/state/products/selectors';

import { createSelector } from 'reselect';
import { fromJS, List } from 'immutable';
import { find } from '~/core/utils/immutable';

export const getFanMode = createSelector(getProductItemById, product => product.getIn(['state', 'fanMode']));

export const getFanCirculatePeriod = createSelector(getProductItemById, product =>
    product.getIn(['state', 'fanCirculate'])
);

export const getDualMode = createSelector(
    getProductItemById,
    product => product && product.getIn(['state', 'dual', 'mode'])
);

export const getThermostatCapabilities = createSelector(getProductItemById, product =>
    product.getIn(['props', 'capabilities'], List())
);

export const getThermostatControlMode = createSelector(getProductItemById, product => {
    const controlMode = product.getIn(['state', 'controlMode']);
    return controlMode
        ? controlMode
        : product.getIn(['props', 'capabilities'], []).includes('heat')
          ? deviceControlModes.HEAT
          : deviceControlModes.COOL;
});

export const getThermostatMode = createSelector(getProductItemById, product => product.getIn(['state', 'mode']));

export const getThermostatStatus = createSelector(getProductItemById, product =>
    product.getIn(['state', 'status'], false)
);

const getControlTypeName = (_, { controlType }) => controlType;

export const getControlTypeSchedule = createSelector(getControlTypeName, getProductItemById, (controlType, product) =>
    product.getIn(['state', `${controlType.toLowerCase()}Schedule`])
);

export const getThermostatHeatTemperature = createSelector(getProductItemById, product =>
    product.getIn(['state', 'heat'])
);

export const getThermostatCoolTemperature = createSelector(getProductItemById, product =>
    product.getIn(['state', 'cool'])
);

export const getHumidity = createSelector(getProductItemById, product => product.getIn(['state', 'humidity']));

export const getCurrentTemperature = createSelector(getProductItemById, product =>
    product.getIn(['props', 'temperature'])
);

export const getCurrentHumidity = createSelector(getProductItemById, product => product.getIn(['props', 'humidity']));

export const getNaThermostats = createSelector(getProductItems, items =>
    items.filter(p => p.get('type') === deviceTypes.NA_THERMOSTAT)
);

export const getOnlineNaThermostats = createSelector(getNaThermostats, stats =>
    stats.filter(s => s.getIn(['props', 'online']) && !s.get('error'))
);

export const getIsAnyMissingHumidifierType = createSelector(getOnlineNaThermostats, nathermos =>
    nathermos.some(p => p.getIn(['state', 'humidifierType']) === deviceHumidityModes.UNKNOWN)
);

export const getKnownHumidifierType = createSelector(getNaThermostats, nathermos => {
    const prod = nathermos
        .filter(p => {
            const type = p.getIn(['state', 'humidifierType']);
            return type && type !== deviceHumidityModes.UNKNOWN;
        })
        .first();
    return prod && prod.getIn(['state', 'humidifierType']);
});

export const getIsUpdating = createSelector(getProductItemById, product => product.get('isUpdating', false));

export const getUpdateType = createSelector(getProductItemById, product => product.get('updateType'));

export const getAllThermostatCapabilities = createSelector(getProductItems, products =>
    products
        .toSeq()
        .filter(p => p.get('type') === deviceTypes.NA_THERMOSTAT)
        .flatMap(p => p.getIn(['props', 'capabilities'], fromJS([])))
        .toSet()
);

export const getAllThermostatCoolTemps = createSelector(
    getProductItems,
    find(p => p.get('type') === deviceTypes.NA_THERMOSTAT && p.getIn(['state', 'cool']))
);

export const getAllThermostatHeatTemps = createSelector(
    getProductItems,
    find(p => p.get('type') === deviceTypes.NA_THERMOSTAT && p.getIn(['state', 'heat']))
);

export const getThermostatHumidity = createSelector(getProductItems, items =>
    find(p => p.get('type') === deviceTypes.NA_THERMOSTAT && p.getIn(['state', 'humidity']), items).getIn([
        'state',
        'humidity'
    ])
);

export const getAllThermostatUpdating = createSelector(
    getProductItems,
    find(p => p.get('type') === deviceTypes.NA_THERMOSTAT && p.getIn(['isUpdating', false]))
);



// WEBPACK FOOTER //
// ./src/core/state/products/thermostat/selectors.js